import { ReactNode, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { FlowIcon } from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { IThreadMessage } from '@forwardflow/features/campaign-flow/hooks/useTestRun'

/* The test phone from Campaign Studio's "Act as lead", in the dashboard's dark palette. */

export const IPhone = ({
   children,
   small,
   testId,
}: {
   children: ReactNode
   small?: boolean
   testId?: string
}) => (
   <div className={cn('cf-iphone', small && 'is-small')} data-testid={testId}>
      <div className="cf-screen">
         <div className="cf-island" />
         <div className="flex justify-between items-center pl-8 pr-7 pt-[16px] h-[36px] text-[14px] font-semibold shrink-0 relative z-[1]">
            <span className="tracking-[-.01em]">9:41</span>
            <span className="flex items-end gap-[2px]">
               {[4, 6, 8, 10].map((h) => (
                  <i
                     key={h}
                     className="w-[3px] rounded-[1px] bg-white"
                     style={{ height: h }}
                  />
               ))}
               <b
                  className="ml-2 w-[24px] h-[11px] rounded-[4px] border-[1.5px] border-white/80"
                  style={{
                     background:
                        'linear-gradient(90deg,#fff 70%,transparent 70%)',
                  }}
               />
            </span>
         </div>
         {children}
      </div>
   </div>
)

export const LockScreen = ({
   day,
   time,
   leadName,
   leadNote,
   hint,
}: {
   day: string
   time: string
   leadName: string
   leadNote: string
   hint: string
}) => (
   <div
      className="flex-1 flex flex-col items-center text-center px-4 pt-8 pb-7"
      data-testid="cf-phone-lock"
   >
      <div className="text-[17px] font-medium text-white/90">{day}</div>
      <div className="text-[82px] leading-[86px] font-light tracking-[-.03em]">
         {time}
      </div>
      <div className="mt-7 w-full cf-ios-blur rounded-[22px] px-3.5 py-3 text-left flex gap-3 items-start">
         <span className="w-9 h-9 rounded-[10px] bg-[#9061DA] flex items-center justify-center text-sm font-bold shrink-0">
            {leadName.charAt(0)}
         </span>
         <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-2">
               <b className="text-[14px] font-semibold">You are {leadName}</b>
               <span className="text-[11px] text-white/55">now</span>
            </span>
            <span className="block text-[13px] text-white/80 leading-[17px]">
               {leadNote}
            </span>
         </span>
      </div>
      <div className="mt-auto text-[13px] leading-[17px] text-white/70 max-w-[220px]">
         {hint}
      </div>
      <div className="mt-4 w-[120px] h-[5px] rounded-full bg-white/80" />
   </div>
)

export const MessagesScreen = ({
   company,
   number,
   when,
   thread,
   highlight,
   onReply,
}: {
   company: string
   number: string
   when: string
   thread: IThreadMessage[]
   highlight?: number
   onReply?: (text: string) => void
}) => {
   const [draft, setDraft] = useState('')
   const ref = useRef<HTMLDivElement | null>(null)
   useEffect(() => {
      ref.current?.scrollTo({
         top: ref.current.scrollHeight,
         behavior: 'smooth',
      })
   }, [thread.length])
   return (
      <div
         className="flex-1 min-h-0 flex flex-col bg-black"
         data-testid="cf-phone-messages"
      >
         <div className="relative flex flex-col items-center gap-0.5 pt-1 pb-2 border-b border-white/[0.1] cf-ios-blur">
            <span className="absolute left-3 top-3 text-[#0a84ff] text-[15px] flex items-center gap-0.5">
               <FlowIcon name="back" size={16} />
               <span className="w-5 h-5 rounded-full bg-[#0a84ff] text-white text-[11px] font-semibold flex items-center justify-center">
                  2
               </span>
            </span>
            <div className="w-[46px] h-[46px] rounded-full bg-[#9061DA] flex items-center justify-center text-[15px] font-semibold">
               {company
                  .split(' ')
                  .map((w) => w[0])
                  .join('')
                  .slice(0, 2)}
            </div>
            <div className="text-[12px] font-medium flex items-center gap-0.5">
               {company}
               <FlowIcon name="chev" size={10} className="text-white/40" />
            </div>
            <div className="text-[10px] text-white/45">{number}</div>
         </div>
         <div
            ref={ref}
            className="flex-1 min-h-0 flex flex-col gap-1.5 px-2.5 pt-2.5 pb-1.5 overflow-y-auto cf-scroll"
         >
            <div className="text-center text-[11px] text-white/45 mb-1 mt-auto">
               <b className="font-semibold">Text Message</b> · {when}
            </div>
            {thread.map((m) => (
               <div
                  key={m.id}
                  className={cn(
                     'cf-bubble-in cf-ios-bubble',
                     m.from === 'lead' ? 'is-me' : 'is-them',
                     m.textIndex !== undefined &&
                        m.textIndex === highlight &&
                        'shadow-[0_0_0_1.5px_#9061DA]'
                  )}
               >
                  {m.text}
               </div>
            ))}
         </div>
         <form
            className="mx-3 mb-5 mt-2 h-9 rounded-full border border-white/20 bg-white/[0.04] flex items-center pl-3.5 pr-1"
            onSubmit={(e) => {
               e.preventDefault()
               onReply?.(draft)
               setDraft('')
            }}
         >
            <input
               value={draft}
               onChange={(e) => setDraft(e.target.value)}
               placeholder={onReply ? 'Reply as Jordan' : 'iMessage'}
               disabled={!onReply}
               className="flex-1 min-w-0 bg-transparent text-[14px] outline-none placeholder:text-white/40"
               data-testid="cf-phone-reply"
            />
            {onReply && (
               <button
                  type="submit"
                  className="w-7 h-7 rounded-full bg-[#0a84ff] flex items-center justify-center"
                  aria-label="Send"
               >
                  <FlowIcon
                     name="arrowRight"
                     size={14}
                     className="shrink-0 -rotate-90"
                  />
               </button>
            )}
         </form>
      </div>
   )
}

export const VoicemailScreen = ({
   from,
   meta,
   transcript,
   playing,
   onPlay,
}: {
   from: string
   meta: string
   transcript: string
   playing: boolean
   onPlay: () => void
}) => (
   <div
      className="flex-1 flex flex-col bg-black px-4 pt-3 pb-5 gap-3"
      data-testid="cf-phone-voicemail"
   >
      <div className="flex items-center justify-between text-[#0a84ff] text-[15px]">
         <span>Greeting</span>
         <span>Edit</span>
      </div>
      <div className="text-[30px] font-bold tracking-[-.02em]">Voicemail</div>
      <div className="flex items-center gap-3 text-[14px]">
         <button
            type="button"
            onClick={onPlay}
            className="w-[38px] h-[38px] rounded-full bg-[#0a84ff] flex items-center justify-center shrink-0"
            aria-label={playing ? 'Stop' : 'Play'}
            data-testid="cf-phone-vm-play"
         >
            <FlowIcon name={playing ? 'pause' : 'play'} size={15} />
         </button>
         <div className="min-w-0">
            <b className="font-semibold text-[15px]">{from}</b>
            <div className="text-[12px] text-white/50 mt-px">{meta}</div>
         </div>
      </div>
      <div className="cf-wave flex items-center gap-[3px] h-[30px]">
         {[
            6, 10, 16, 9, 18, 22, 12, 8, 14, 20, 11, 7, 15, 19, 10, 6, 12, 17,
            9, 5, 11, 14, 8, 6,
         ].map((h, i) => (
            <i
               key={i}
               style={{
                  height: h,
                  background: playing && i < 12 ? '#DAC2FF' : undefined,
               }}
            />
         ))}
      </div>
      <div className="text-[14px] leading-[19px] text-white/90 bg-[#1c1c1e] rounded-[12px] px-3.5 py-3">
         <span className="block text-[11px] tracking-[.06em] uppercase text-white/45 font-semibold mb-1">
            Transcription
         </span>
         “{transcript}”
      </div>
      <div className="mt-auto pt-3 border-t border-white/[0.1] flex items-center gap-3 text-[14px] opacity-45">
         <span className="w-[38px] h-[38px] rounded-full bg-white/10 flex items-center justify-center">
            <FlowIcon name="play" size={15} />
         </span>
         <div>
            <b className="font-semibold">Mom</b>
            <div className="text-[12px] text-white/50">Yesterday · 0:42</div>
         </div>
      </div>
   </div>
)

/** Caption above the phone + phone + control row below it. */
export const PhoneColumn = ({
   caption,
   live,
   small,
   children,
   controls,
   testId,
}: {
   caption: string
   live?: boolean
   small?: boolean
   children: ReactNode
   controls: ReactNode
   testId?: string
}) => (
   <div
      className={cn(
         'flex flex-col items-center gap-2.5 shrink-0 mx-auto',
         small ? 'min-w-[236px]' : 'min-w-[262px]'
      )}
      data-testid={testId}
   >
      <div
         className={cn(
            'text-[11px] tracking-[.08em] uppercase font-semibold flex items-center gap-1.5 h-4',
            live ? 'text-[#00d857]' : 'text-[#9ca3af]'
         )}
      >
         <FlowIcon name="phone" size={12} /> {caption}
      </div>
      <IPhone small={small}>{children}</IPhone>
      <div className="flex gap-1.5 justify-center flex-nowrap">{controls}</div>
   </div>
)
